import { useRef, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Select,
  SelectItem,
  Progress,
  addToast,
} from '@heroui/react';
import { FiFlag, FiUpload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../feature-module/components/CustomButton';
import { useAuth } from '../../context/AuthContext';
import { handleLogin } from '../../feature-module/components/auth/apiAuth';
import {
  submitReport,
  type ReportReason,
  type ReportTargetType,
} from '../../service/reportService';
import {
  uploadToS3WithImageName,
  deleteFromS3ByImageName,
} from '../../feature-module/frontend/Customer/aws/s3FileUpload';

const REASONS: ReportReason[] = [
  'fraud',
  'inappropriate',
  'no_show',
  'safety',
  'quality',
  'other',
];

const MAX_EVIDENCE = 5;
const EVIDENCE_FOLDER = 'report-images';

interface ReportButtonProps {
  targetType: ReportTargetType;
  targetId: string;
  variant?: 'icon' | 'link';
  className?: string;
}

interface EvidenceItem {
  url: string;
  imageName: string;
}

/**
 * Shared "Report" affordance for the customer portal. Opens a modal to pick a
 * reason, an optional description and up to 5 evidence images (uploaded via the
 * existing S3 flow), then POSTs to the trust-safety service.
 *
 * Reports are authenticated-only: guests are bounced to login rather than shown
 * the form. There is no business scope — see reportService.ts.
 */
const ReportButton = ({
  targetType,
  targetId,
  variant = 'link',
  className = '',
}: ReportButtonProps) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setReason(null);
    setDescription('');
    setEvidence([]);
    setError(null);
  };

  const handleOpen = () => {
    if (!isAuthenticated) {
      addToast({
        title: t('report.auth_required_title'),
        description: t('report.auth_required_description'),
        radius: 'md',
        color: 'danger',
      });
      handleLogin();
      return;
    }
    if (!targetId) {
      addToast({
        title: t('report.unavailable_title'),
        description: t('report.unavailable_description'),
        radius: 'md',
        color: 'warning',
      });
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    if (submitting || uploading) return;
    setOpen(false);
  };

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const remaining = MAX_EVIDENCE - evidence.length;
    if (files.length > remaining) {
      setError(t('report.max_evidence', { max: MAX_EVIDENCE }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const uploaded: EvidenceItem[] = [];
      for (let i = 0; i < files.length; i++) {
        const result = await uploadToS3WithImageName(files[i], EVIDENCE_FOLDER);
        uploaded.push({ url: result.fullUrl, imageName: result.imageName });
        setUploadProgress(((i + 1) / files.length) * 100);
      }
      setEvidence((prev) => [...prev, ...uploaded]);
    } catch (uploadErr: any) {
      console.error('Error uploading report evidence:', uploadErr);
      setError(t('report.upload_error'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeEvidence = async (index: number) => {
    const item = evidence[index];
    setEvidence((prev) => prev.filter((_, i) => i !== index));
    if (item?.imageName) {
      try {
        await deleteFromS3ByImageName(item.imageName, EVIDENCE_FOLDER);
      } catch {
        // Best-effort cleanup — the UI already reflects the removal.
      }
    }
  };

  const handleSubmit = async () => {
    if (!reason) {
      setError(t('report.reason_required'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitReport({
        targetType,
        targetId,
        reason,
        description: description.trim() || undefined,
        evidenceUrls: evidence.length
          ? evidence.map((item) => item.url)
          : undefined,
      });

      addToast({
        title: t('report.success_title'),
        description: t('report.success_description'),
        radius: 'md',
        color: 'success',
      });

      reset();
      setOpen(false);
    } catch (submitErr: any) {
      console.error('Report submission failed:', submitErr);
      setError(
        submitErr?.response?.data?.message ||
          submitErr?.message ||
          t('report.submit_error')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reasonLabel = (r: ReportReason) => t(`report.reasons.${r}`);

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label={t('report.trigger_label')}
          title={t('report.trigger_label')}
          className={`inline-flex items-center rounded p-1.5 text-brandslate transition hover:bg-destructive/10 hover:text-destructive ${className}`}
        >
          <FiFlag size={16} aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          className={`inline-flex items-center gap-1 text-xs text-brandslate transition hover:text-destructive ${className}`}
        >
          <FiFlag size={13} aria-hidden />
          {t('report.trigger_label')}
        </button>
      )}

      <Modal isOpen={open} onClose={handleClose} size="2xl">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span className="text-brandnavy">{t('report.title')}</span>
          </ModalHeader>

          <ModalBody>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {/* Left column — reason (required) + description */}
              <div className="flex flex-col gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-brandnavy">
                    {t('report.reason_label')}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <Select
                    placeholder={t('report.reason_placeholder')}
                    variant="bordered"
                    radius="sm"
                    className="w-full"
                    classNames={{
                      trigger:
                        'border border-gray-300 bg-white hover:border-gray-400',
                      value: 'text-sm text-gray-700',
                    }}
                    selectedKeys={reason ? [reason] : []}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as
                        | ReportReason
                        | undefined;
                      setReason(value ?? null);
                      setError(null);
                    }}
                  >
                    {REASONS.map((r) => (
                      <SelectItem key={r}>{reasonLabel(r)}</SelectItem>
                    ))}
                  </Select>
                </div>

                <Textarea
                  label={t('report.description_label')}
                  labelPlacement="outside"
                  variant="bordered"
                  radius="sm"
                  placeholder={t('report.description_placeholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  minRows={4}
                />
              </div>

              {/* Right column — evidence upload */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-body">
                    {t('report.evidence_label')}
                  </span>
                  <span className="text-caption text-gray-500">
                    {evidence.length}/{MAX_EVIDENCE}
                  </span>
                </div>

                {uploading && (
                  <Progress
                    value={uploadProgress}
                    size="sm"
                    color="primary"
                    className="mb-3"
                  />
                )}

                {evidence.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {evidence.map((item, idx) => (
                      <div key={idx} className="group relative">
                        <img
                          src={item.url}
                          alt={t('report.evidence_alt', { index: idx + 1 })}
                          className="h-16 w-16 rounded-lg border border-gray-200 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeEvidence(idx)}
                          disabled={uploading || submitting}
                          aria-label={t('report.remove_evidence')}
                          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white opacity-0 shadow transition-opacity group-hover:opacity-100"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {evidence.length < MAX_EVIDENCE && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || submitting}
                    className="flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center transition-colors hover:border-primary disabled:opacity-50"
                  >
                    <FiUpload className="text-gray-400" size={20} />
                    <span className="text-sm font-medium text-gray-600">
                      {uploading
                        ? t('report.uploading')
                        : t('report.evidence_upload_cta')}
                    </span>
                    <span className="text-caption text-gray-500">
                      {t('report.evidence_hint')}
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFiles}
                  className="hidden"
                />

                {error && (
                  <p className="mt-2 text-sm text-destructive">{error}</p>
                )}
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <CustomButton
              label={t('report.cancel')}
              color="default"
              variant="light"
              onPress={handleClose}
              isDisabled={submitting || uploading}
            />
            <CustomButton
              label={submitting ? t('report.submitting') : t('report.submit')}
              color="primary"
              onPress={handleSubmit}
              isLoading={submitting}
              isDisabled={submitting || uploading}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ReportButton;
